import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
} from "../components/table";
import { Button } from "../components/button";
import { Heading } from "../components/heading";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "../components/dropdown";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogDescription,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../components/dialog";
import { ErrorMessage, Field, Label } from "../components/fieldset";
import { Input } from "../components/input";
import { db } from "../firebase";
import EmptyState from "./components/empty-state";
import { UsersIcon } from "@heroicons/react/24/outline";
import {
  doc,
  setDoc,
  onSnapshot,
  query,
  collection,
  deleteDoc,
} from "firebase/firestore";

export default function Accounts() {
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [users, setUsers] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('')
  const [errors, setErrors] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = () => {
      const q = query(collection(db, "admins"));
      onSnapshot(q, (querySnapshot) => {
        const usersArray: string[] = [];
        querySnapshot.forEach((doc) => {
          usersArray.push(doc.data().email);
        });
        setUsers(usersArray);
      });
    };

    fetchData();
  }, []);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const inviteUser = async () => {
    setErrors(null);

    if (!isValidEmail(newEmail)) {
      setErrors("Invalid email format.");
      return
    }

    const collectionRef = doc(db, "admins", newEmail);
    const newUser = { email: newEmail };

    setUsers([...users, newEmail]);

    setIsInviteDialogOpen(false)

    await setDoc(collectionRef, newUser);
  };

  const deleteUser = async (email: string) => {
    const collectionRef = doc(db, "admins", email);
    setUsers([...users.filter((user) => user !== email)]);

    await deleteDoc(collectionRef);
  };

  return (
    <div>
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>Accounts</Heading>
        <div className="flex gap-4">
          <Button onClick={() => setIsInviteDialogOpen(true)}>
            Invite User
          </Button>
        </div>
      </div>

      {users.length > 0 ? (
        <Table className="[--gutter:theme(spacing.6)] sm:[--gutter:theme(spacing.8)]">
          <TableHead>
            <TableRow>
              <TableHeader>Email</TableHeader>
              <TableHeader>Access</TableHeader>
              <TableHeader className="relative w-0">
                <span className="sr-only">Actions</span>
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user}>
                <TableCell>{user}</TableCell>
                <TableCell className="text-zinc-500">Admin</TableCell>
                <TableCell>
                  <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                    <Dropdown>
                      <DropdownButton plain aria-label="More options">
                        <EllipsisHorizontalIcon />
                      </DropdownButton>
                      <DropdownMenu anchor="bottom end">
                        <DropdownItem onClick={() => deleteUser(user)}>Delete</DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          title={"No Users"}
          description={"Invite users to start collaborating"}
        >
          <UsersIcon className="w-10 h-10" />
        </EmptyState>
      )}

      <Dialog open={isInviteDialogOpen} onClose={setIsInviteDialogOpen}>
        <DialogTitle>Invite User</DialogTitle>
        <DialogDescription>
          This user will join as an admin and have full access to the account.
          They will be able to invite other users.
        </DialogDescription>
        <DialogBody>
          <Field>
            <Label>Email Address</Label>
            <Input autoComplete="email" type="email" name="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="example@gmail.com" />
            {errors && <ErrorMessage>{errors}</ErrorMessage>}
          </Field>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsInviteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => { inviteUser() }}>Invite</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
